import React, { useRef, useState } from 'react';
import { Table, Segment, Icon, Button } from 'semantic-ui-react';
import { Flexbox, FlexItem, StyledContainer } from '@src/views/containers';
import { FlexAlign, FlexJustify } from '@src/types';
import './Chart.css';
import './styles/DashCharts.scss';

interface TableRow {
    [key: string]: string | number;
}

interface DashTableProps {
    data: TableRow[];
    title: string;
}

const DashTable: React.FC<DashTableProps> = (props) => {
    const [ filteredData, setFilteredData ]             = useState<TableRow[] | null>(props.data),
          [ sortState, setSortState ]                   = useState<Record<string, 'asc' | 'desc' | null>>( {} ),
          [ activeFilterColumn, setActiveFilterColumn ] = useState<string | null>( null ),
          [ filterState, setFilterState ]               = useState<Record<string, Set<string>>>( {} ),
          filterRef                                     = useRef<HTMLDivElement | null>( null );

    if (!props.data || !Array.isArray(props.data) || props.data.length === 0) {
        return (
            <Segment>
                <p>No data available</p>
            </Segment>
        );
    }

    if (!filteredData && props.data && Array.isArray(props.data) && props.data.length > 0) {
        setFilteredData(props.data);

        if (props.data[0]) {
            const initialSortState: Record<string, 'asc' | 'desc' | null> = {};
            Object.keys(props.data[0]).forEach(column => {
                initialSortState[column] = null;
            });
            setSortState(initialSortState);

            const initialFilterState: Record<string, Set<string>> = {};
            Object.keys(props.data[0]).forEach(column => {
                initialFilterState[column] = new Set(props.data.map(row => String(row[column])));
            });
            setFilterState(initialFilterState);
        }
    }

    const handleSort = (column: string) => {
        const currentSort = sortState[column];
        const newSort: 'asc' | 'desc' | null = currentSort === 'asc' ? 'desc' : currentSort === 'desc' ? null : 'asc';

        setSortState(prev => ({ ...prev, [column]: newSort }));

        if (filteredData) {
            const sortedData = [...filteredData].sort((a, b) => {
                if (newSort === 'asc') return String(a[column]) > String(b[column]) ? 1 : -1;
                if (newSort === 'desc') return String(a[column]) < String(b[column]) ? 1 : -1;
                return 0;
            });
            setFilteredData(newSort ? sortedData : props.data);
        }
    };

    const handleCheckboxChange = (column: string, value: string) => {
        setFilterState((prev) => {
            const updatedSet = new Set(prev[column]);
            if (updatedSet.has(value)) {
                updatedSet.delete(value);
            } else {
                updatedSet.add(value);
            }

            const updatedFilterState = { ...prev, [column]: updatedSet };

            const newFilteredData = props.data.filter((row) =>
                Object.keys(updatedFilterState).every((col) =>
                    updatedFilterState[col].size === 0 || updatedFilterState[col].has(String(row[col]))
                )
            );

            setFilteredData(newFilteredData);
            return updatedFilterState;
        });
    };

    const renderFilterDropdown = (column: string) => {
        const distinctValues = Array.from(new Set(props.data.map((row) => String(row[column]))));
        const isChecked = (value: string) => filterState[column]?.has(value) || false;

        return (
            <StyledContainer ref={filterRef} className="dash-table-filter">
                {distinctValues.map((value) => (
                    <label key={value} className="dash-table-filter-option">
                        <input
                            type="checkbox"
                            checked={isChecked(value)}
                            onChange={() => handleCheckboxChange(column, value)}
                        />
                        {value}
                    </label>
                ))}
                <Button size="mini" className="dash-table-filter-close" onClick={() => setActiveFilterColumn(null)}>
                    Close
                </Button>
            </StyledContainer>
        );
    };

    const columnNames = Object.keys(props.data[0]);
    const dataToDisplay = filteredData || props.data;

    if (!Array.isArray(dataToDisplay)) {
        return (
            <Segment>
                <p>Invalid data format</p>
            </Segment>
        );
    }

    return (
        <StyledContainer className="dash-table-wrap">
            <Table celled striped className="dash-table">
                <Table.Header>
                    <Table.Row>
                        {columnNames.map((column) => (
                            <Table.HeaderCell key={column}>
                                <Flexbox
                                    align={FlexAlign.CENTER}
                                    justify={FlexJustify.SPACE_BETWEEN}
                                    className="dash-table-header-cell"
                                >
                                    {column}
                                    <FlexItem noGrow>
                                        <Icon
                                            name={sortState[column] === 'asc' ? 'sort up' : sortState[column] === 'desc' ? 'sort down' : 'sort'}
                                            onClick={() => handleSort(column)}
                                            className="dash-table-action-icon"
                                        />
                                        <Icon
                                            name="filter"
                                            onClick={() => setActiveFilterColumn(column)}
                                            className="dash-table-action-icon"
                                        />
                                    </FlexItem>
                                </Flexbox>
                                {activeFilterColumn === column && renderFilterDropdown(column)}
                            </Table.HeaderCell>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {dataToDisplay.map((row: TableRow, rowIndex) => (
                        <Table.Row key={rowIndex}>
                            {columnNames.map((column) => (
                                <Table.Cell key={column}>{String(row[column])}</Table.Cell>
                            ))}
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </StyledContainer>
    );
};

export default DashTable;
